import { useMemo } from "react"

import type { RefCast } from "@productlane/lib"
import {
	jsonTemplateParses,
	misplacedTypedTokens,
	wholeTemplateType,
} from "@productlane/lib"
import { Button } from "@productlane/ui"

import type { SecureRefOption } from "../model/secure-ref-picker"
import { FieldNotice } from "../inputs/field-notice"
import { replaceRef, staleHttpRefs } from "../model/secure-ref-picker"

interface Props {
	value: string
	mode: "ref" | "template"
	options: readonly SecureRefOption[]
	/** Property names the argument declares, for the token-collision notice. */
	knownKeys?: readonly string[]
	onRewrite: (next: string) => void
	cast?: RefCast
}

/** Everything one step of a chain can be wrong about, in the order it matters. */
export function StepNotices({
	value,
	mode,
	options,
	knownKeys,
	onRewrite,
	cast,
}: Props) {
	return (
		<>
			<StaleRefNotice
				value={value}
				mode={mode}
				options={options}
				onRewrite={onRewrite}
			/>
			<TypedTokenNotice
				value={value}
				mode={mode}
				knownKeys={knownKeys}
				onRewrite={onRewrite}
				cast={cast}
			/>
			<JsonTemplateNotice value={value} mode={mode} cast={cast} />
		</>
	)
}

/**
 * A JSON template is parsed once its tokens carry values, so a stray comma or
 * quote refuses every call. It cannot be saved either, hence the red.
 */
function JsonTemplateNotice({
	value,
	mode,
	cast,
}: Pick<Props, "value" | "mode" | "cast">) {
	const broken = useMemo(
		() =>
			mode === "template" &&
			cast === "json" &&
			value.trim().length > 0 &&
			!jsonTemplateParses(value),
		[cast, mode, value],
	)
	if (!broken) return null

	return (
		<FieldNotice tone="error">
			This template is not valid JSON, so it cannot be saved and the agent could
			never call the tool. Check the commas and quotes.
		</FieldNotice>
	)
}

/**
 * A typed token only carries its type when nothing surrounds it, and a keyword
 * that also names a child argument is a collision only the schema can spot.
 */
function TypedTokenNotice({
	value,
	mode,
	knownKeys,
	onRewrite,
	cast,
}: Omit<Props, "options">) {
	const misplaced = useMemo(
		() =>
			mode === "template"
				? misplacedTypedTokens({ mode: "template", template: value }, cast)[0]
				: undefined,
		[cast, mode, value],
	)
	const wholeType = useMemo(
		() => (mode === "template" ? wholeTemplateType(value) : undefined),
		[mode, value],
	)

	if (misplaced) {
		return (
			<FieldNotice>
				{`{{${misplaced}}} sets the value's type, so it has to be the whole value rather than sit inside text.`}
			</FieldNotice>
		)
	}

	if (wholeType && knownKeys?.includes(wholeType)) {
		return (
			<FieldNotice
				action={
					<Button
						variant="outline"
						size="xs"
						onClick={() => onRewrite(`{{agent.["${wholeType}"]}}`)}
					>
						Read the property
					</Button>
				}
			>
				{`This argument also has a "${wholeType}" property. The token casts the value instead of reading it.`}
			</FieldNotice>
		)
	}

	return null
}

function StaleRefNotice({
	value,
	mode,
	options,
	onRewrite,
}: Pick<Props, "value" | "mode" | "options" | "onRewrite">) {
	const stale = useMemo(
		() => staleHttpRefs(value, mode, options),
		[value, mode, options],
	)
	if (stale.length === 0) return null

	return (
		<>
			{stale.map((entry) => (
				<FieldNotice
					key={entry.ref}
					action={entry.matches.map((match) => (
						<Button
							key={match.ref}
							variant="outline"
							size="xs"
							onClick={() =>
								onRewrite(replaceRef(value, mode, entry.ref, match.ref))
							}
						>
							{match.connectorTitle ?? "Connector"}
						</Button>
					))}
				>
					{entry.matches.length === 0
						? `No connector sends "${entry.ref}", so this rule refuses every call. Pick a value from the list.`
						: `"${entry.ref}" names no connector, so this rule refuses every call. It comes from:`}
				</FieldNotice>
			))}
		</>
	)
}
